import { useEffect, useState, useLayoutEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"

import axios from "axios"
import { useAuth } from "contexts/auth-context"
import Form from "./form"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { useSWRConfig } from "swr"
import { usePage } from "contexts/page-context"
import moment from "moment"
import { useAuditLogContext } from "contexts/audit-log-context"

/**
 * Helper: bangun FormData dengan key bracket (images[i][file])
 */
function buildFormData(data) {
  const fd = new FormData()

  // field model
  if (data.id) fd.append("id", data.id)
  fd.append("product_series_id", data.product_series_id ?? "")
  // opsional: kirim nama series kalau kamu perlukan untuk logging backend
  if (data.product_series?.series?.name) {
    fd.append("product_series", data.product_series.series.name)
  }

  fd.append("name", data.name ?? "")
  fd.append("long_name", data.long_name ?? "")
  fd.append("description", data.description ?? "")
  if (data.created_by) fd.append("created_by", data.created_by)

  // tags (sesuaikan bentuk yang dibaca backend kamu)
  // di sini aku kirim sebagai array sederhana
  ;(data.tags ?? []).forEach((t, i) => {
    const v = typeof t === "string" ? t : (t.slug ?? "")
    if (v !== "") fd.append(`tags[${i}]`, v)
  })

  // images
  ;(data.images ?? []).forEach((img, i) => {
    if (img.id) fd.append(`images[${i}][id]`, img.id)
    fd.append(`images[${i}][_action]`, img._action ?? "none")
    fd.append(`images[${i}][alt_text]`, img.alt_text ?? "")
    fd.append(`images[${i}][is_cover]`, img.is_cover ? 1 : 0)
    fd.append(
      `images[${i}][sort_number]`,
      Number.isFinite(img.sort_number) ? img.sort_number : i
    )
    if (img.file instanceof File) {
      fd.append(`images[${i}][file]`, img.file, img.file.name)
    }
  })

  return fd
}

/**
 * Helper: data aman untuk audit log (FormData tidak bisa di-JSON.stringify)
 */
function safeAuditPayload(raw) {
  const pick = {
    product_series_id: raw.product_series_id ?? null,
    product_series: raw.product_series?.series?.name ?? null,
    name: raw.name ?? null,
    long_name: raw.long_name ?? null,
    description: raw.description ?? null,
    created_by: raw.created_by ?? null,
    tags: (raw.tags ?? []).map((t) => (typeof t === "string" ? t : t?.slug ?? "")),
    images: (raw.images ?? []).map((x) => ({
      id: x.id ?? null,
      alt_text: x.alt_text ?? "",
      is_cover: !!x.is_cover,
      sort_number: x.sort_number ?? null,
      _action: x._action ?? "none",
      // jangan kirim file binary ke audit
      file_name: x.file instanceof File ? x.file.name : null,
      file_size: x.file instanceof File ? x.file.size : null,
      file_type: x.file instanceof File ? x.file.type : null,
    })),
  }
  return pick
}

export default function Create() {
  const { user, isSuper } = useAuth()
  const { cache } = useSWRConfig()
  const { queueAuditLog } = useAuditLogContext()
  const { setTransition } = usePage()

  const location = useLocation()
  const navigate = useNavigate()

  const [prevRouteState, setPrevRouteState] = useState({})
  const [errors, setErrors] = useState({})

  useLayoutEffect(() => {
    const getPrevRouteState = async () => {
      setPrevRouteState({
        back: location.state?.back,
        from: location.state?.from,
        transition: location.state?.transition
      })
    }

    setTransition(location.state?.transition)
    getPrevRouteState()
  }, [])

  const handleSubmit = async (formDataFromChild) => {
    setErrors({})
    nProgress.start()
    toast.loading("Loading", { id: "submit" })

    const url = `${process.env.REACT_APP_INVENTORY_API_URL}/product/model`

    // payload mentah untuk audit (non-FormData)
    const rawPayload = {
      product_series_id: formDataFromChild.product_series_id,
      product_series: formDataFromChild.product_series, // object (opsional)
      name: formDataFromChild.name,
      description: formDataFromChild.description,
      long_name: formDataFromChild.long_name,
      created_by: user?.name,
      tags: formDataFromChild.tags,
      images: formDataFromChild.images,
    }

    // FormData untuk request (multipart)
    const fd = buildFormData({
      ...rawPayload,
    })

    try {
      const response = await axios.post(url, fd, {
        // Biarkan axios set Content-Type + boundary sendiri.
        // headers: { 'Content-Type': 'multipart/form-data' },
      })

      // Queue audit log (pakai payload aman)
      queueAuditLog(
        url,
        "POST",
        "Create Product Model Data",
        user?.name,
        safeAuditPayload(rawPayload),
        null,
        moment().format("YYYY-MM-DD HH:mm:ss"),
        response.status
      )

      // Clear SWR cache
      cache.clear()

      // Navigate page
      navigate({ pathname: "/product/model" })

      toast.success(response.data.message ?? "Created", { id: "submit" })
    } catch (error) {
      // Assign validation message
      if (error?.response?.status === 422) {
        setErrors(error.response.data.errors ?? {})
      }

      toast.error(error?.response?.data?.message || "Failed", {
        ariaProps: { superscript: error?.response?.status },
        id: "submit",
      })
    } finally {
      nProgress.done()
    }
  }

  return (
    <Form errors={errors} onSubmit={handleSubmit} />
  )
}
